import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  acceptApplicant,
  declineApplicant,
  getEventApplicants,
  reopenSlot,
} from "../../api/opportunities";
import ProfilePicture from "../icons/ProfilePicture";
import { enqueueSnackbar } from "notistack";
import { FiCheck, FiUsers, FiX } from "react-icons/fi";
import {
  formatOccurrenceDate,
  formatSlotOccurrenceTimeRange,
} from "../../util/eventDateTime";

const summarizeSlots = (slots = []) => {
  const applications = slots.flatMap((slot) => slot.applications || []);

  return {
    slotDetails: slots,
    totalApplicants: applications.length,
    pendingApplicants: applications.filter((app) => app.status === "pending").length,
    acceptedApplicants: applications.filter((app) => app.status === "accepted").length,
  };
};

const getApplicantIdentity = (app = {}) => app.applicant || app.applicantSnapshot || {};

const getApplicantName = (app = {}) => {
  const identity = getApplicantIdentity(app);
  return identity.name || identity.username || identity.email || "deleted account";
};

const toId = (value) => String(value?._id || value || "");

const occurrenceKey = (value) => new Date(value).getTime();

const rosterRoute = (rosterId) =>
  `/dashboard?tab=bookings&bookingsTab=rosters&rosterId=${rosterId}&rosterTab=members`;

// clickable roster pill; deleted rosters render as plain (non-clickable) pills
const RosterBubble = ({ rosterId, label, tone = "default", disabled = false, title, onOpen }) => {
  const toneClass =
    tone === "warning"
      ? "border-warning-line text-warning"
      : "border-[var(--inyc-input-border-color)] text-[var(--inyc-primary-text-color-light)]";
  const content = (
    <>
      <FiUsers size={10} className="shrink-0" />
      <span className="truncate">{label}</span>
    </>
  );
  const className = `inline-flex max-w-full items-center gap-kit-4 rounded-kit-full border-[1px] px-kit-8 py-kit-2 text-kit-2xs font-['Primary-Medium'] ${toneClass}`;

  if (disabled || !rosterId) {
    return <span className={className} title={title}>{content}</span>;
  }
  return (
    <button
      type="button"
      title={title || `open ${label}`}
      onClick={(event) => {
        event.stopPropagation();
        onOpen(rosterId);
      }}
      className={`${className} transition-colors hover:border-[var(--inyc-accent)] hover:text-[var(--inyc-accent)]`}
    >
      {content}
    </button>
  );
};

const LineupManager = ({
  eventId,
  compact = false,
  className = "",
  onSlotsChange,
  contextRosterId,
  initialOccurrenceStart,
}) => {
  const [slots, setSlots] = useState([]);
  const [eventRoster, setEventRoster] = useState(null);
  const [eventMeta, setEventMeta] = useState(null);
  const [occurrenceStart, setOccurrenceStart] = useState(null);
  const [occurrences, setOccurrences] = useState([]);
  const [isRecurring, setIsRecurring] = useState(false);
  const [expandedApplicationSlots, setExpandedApplicationSlots] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const openRoster = (rosterId) => navigate(rosterRoute(rosterId));

  const publishSlots = useCallback(
    (nextSlots) => {
      onSlotsChange?.(summarizeSlots(nextSlots));
    },
    [onSlotsChange]
  );

  const fetch = useCallback(
    async (occurrenceOverride) => {
      if (!eventId) return;
      setLoading(true);
      try {
        const data = await getEventApplicants(eventId, occurrenceOverride || occurrenceStart);
        const nextSlots = data.slots || [];
        setEventRoster(data.eventRoster || null);
        setEventMeta({ date: data.eventDate, timezone: data.eventTimezone });
        setOccurrences(data.occurrences || []);
        setIsRecurring(Boolean(data.isRecurring));
        if (data.occurrenceStart) {
          setOccurrenceStart(data.occurrenceStart);
        }
        setSlots(nextSlots);
        publishSlots(nextSlots);
      } catch {
        enqueueSnackbar("Failed to load lineup", { variant: "error" });
      } finally {
        setLoading(false);
      }
    },
    [eventId, occurrenceStart, publishSlots]
  );

  useEffect(() => {
    if (!eventId) return;
    setOccurrenceStart(initialOccurrenceStart || null);
    setExpandedApplicationSlots({});
  }, [eventId, initialOccurrenceStart]);

  useEffect(() => {
    if (!eventId) return;
    fetch(initialOccurrenceStart || undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, initialOccurrenceStart]);

  const updateSlots = (updater) => {
    setSlots((prev) => {
      const nextSlots = updater(prev);
      publishSlots(nextSlots);
      return nextSlots;
    });
  };

  const handleOccurrenceChange = (nextOccurrenceStart) => {
    setOccurrenceStart(nextOccurrenceStart);
    fetch(nextOccurrenceStart);
  };

  const handleAccept = async (slotId, applicantId) => {
    try {
      await acceptApplicant(slotId, applicantId, occurrenceStart);
      enqueueSnackbar("Performer accepted", { variant: "success" });
      updateSlots((prev) =>
        prev.map((slot) => {
          if (slot._id !== slotId) return slot;
          return {
            ...slot,
            status: "filled",
            filledBy: applicantId,
            applications: slot.applications.map((app) =>
              app.applicant?._id === applicantId ? { ...app, status: "accepted" } : app
            ),
          };
        })
      );
    } catch {
      enqueueSnackbar("Failed to accept", { variant: "error" });
    }
  };

  const handleDecline = async (slotId, applicantId) => {
    try {
      await declineApplicant(slotId, applicantId, occurrenceStart);
      enqueueSnackbar("Applicant declined", { variant: "success" });
      updateSlots((prev) =>
        prev.map((slot) => {
          if (slot._id !== slotId) return slot;
          return {
            ...slot,
            applications: slot.applications.map((app) =>
              app.applicant?._id === applicantId ? { ...app, status: "declined" } : app
            ),
          };
        })
      );
    } catch {
      enqueueSnackbar("Failed to decline", { variant: "error" });
    }
  };

  const handleReopen = async (slotId) => {
    try {
      await reopenSlot(slotId, occurrenceStart);
      enqueueSnackbar("Slot reopened", { variant: "success" });
      updateSlots((prev) =>
        prev.map((slot) => {
          if (slot._id !== slotId) return slot;
          return {
            ...slot,
            status: "open",
            filledBy: null,
            applications: slot.applications.map((app) =>
              app.status === "accepted" ? { ...app, status: "cancelled" } : app
            ),
          };
        })
      );
    } catch {
      enqueueSnackbar("Failed to reopen slot", { variant: "error" });
    }
  };

  const toggleOtherApplications = (slotId) => {
    setExpandedApplicationSlots((prev) => ({
      ...prev,
      [slotId]: !prev[slotId],
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-kit-32 text-[var(--inyc-primary-text-color-light)]">
        loading lineup...
      </div>
    );
  }

  if (slots.length === 0) {
    return (
      <div className="py-kit-32 text-center text-kit-sm text-[var(--inyc-primary-text-color-light)]">
        no lineup slots on this event yet. add slots in the event tab.
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col ${
        compact ? "gap-kit-12" : "gap-kit-24 max-w-[90%] md:max-w-[500px] mx-auto py-kit-24"
      } ${className}`}
    >
      {isRecurring && occurrences.length > 1 && (
        <div className="flex gap-kit-8 overflow-x-auto pb-kit-2">
          {occurrences.map((row) => {
            const active =
              occurrenceStart &&
              occurrenceKey(row.occurrenceStart) === occurrenceKey(occurrenceStart);
            return (
              <button
                key={occurrenceKey(row.occurrenceStart)}
                type="button"
                onClick={() => handleOccurrenceChange(row.occurrenceStart)}
                className={`shrink-0 rounded-kit-full border-[1px] px-kit-12 py-kit-6 text-kit-2xs font-['Primary-Medium'] transition-colors ${
                  active
                    ? "border-[var(--inyc-accent)] bg-[var(--inyc-accent)]/15 text-[var(--inyc-accent)]"
                    : "border-[var(--inyc-input-border-color)] text-[var(--inyc-primary-text-color-light)] hover:border-[var(--inyc-accent)]"
                }`}
              >
                {formatOccurrenceDate(row.occurrenceStart, eventMeta)}
              </button>
            );
          })}
        </div>
      )}

      {slots.map((slot) => {
        const slotRosterId = toId(slot.roster) || toId(eventRoster);
        const isOutsideContextRoster =
          contextRosterId && slotRosterId && slotRosterId !== toId(contextRosterId);
        const applications = slot.applications || [];
        const filledById = toId(slot.filledBy);
        const isAcceptedApplication = (app) =>
          app.status === "accepted" || (filledById && toId(app.applicant) === filledById);
        const acceptedApplications = applications.filter(isAcceptedApplication);
        const otherApplications = applications.filter((app) => !isAcceptedApplication(app));
        const showOtherApplications = Boolean(expandedApplicationSlots[slot._id]);
        const visibleOtherApplications = showOtherApplications
          ? otherApplications
          : otherApplications.slice(0, 1);
        const hiddenOtherCount = Math.max(
          0,
          otherApplications.length - visibleOtherApplications.length
        );
        const renderApplication = (app) => {
          const identity = getApplicantIdentity(app);
          const missingAccount = !app.applicant?._id;

          return (
            <div
              key={app._id}
              className={`flex items-center gap-kit-12 ${
                compact ? "px-kit-12 py-kit-12" : "px-kit-20 py-kit-16"
              }`}
            >
              <ProfilePicture
                username={identity.username || identity.name || identity.email}
                profilePicture={identity.profilePicture}
                style={{
                  width: compact ? "2rem" : "2.25rem",
                  height: compact ? "2rem" : "2.25rem",
                  fontSize: "var(--inyc-text-2xs)",
                  flexShrink: 0,
                }}
              />
              <div className="min-w-0 flex-1">
                <div className="truncate font-['Primary-Medium'] text-kit-sm text-[var(--inyc-primary-text-color)]">
                  {getApplicantName(app)}
                </div>
                {missingAccount && (
                  <div className="truncate text-kit-2xs text-[var(--inyc-primary-text-color-light)]">
                    account deleted
                  </div>
                )}
                {!missingAccount && slot.rosterName && (
                  <div className="mt-kit-4 flex min-w-0">
                    <RosterBubble
                      rosterId={slotRosterId}
                      onOpen={openRoster}
                      disabled={slot.rosterDeleted}
                      tone={app.onRoster === false ? "warning" : "default"}
                      label={
                        slot.rosterDeleted
                          ? `${slot.rosterName} · roster deleted`
                          : app.onRoster === false
                          ? `removed from ${slot.rosterName}`
                          : slot.rosterName
                      }
                    />
                  </div>
                )}
                {app.note && (
                  <div className="truncate text-kit-2xs text-[var(--inyc-primary-text-color-light)]">
                    "{app.note}"
                  </div>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-kit-6">
                {app.status !== "pending" && (
                  <span
                    className={`rounded-kit-full px-kit-8 py-kit-4 text-kit-2xs font-['Primary-Medium'] ${
                      app.status === "accepted"
                        ? "bg-success-soft text-success"
                        : app.status === "cancelled"
                        ? "bg-neutral-soft text-neutral"
                        : "bg-danger-soft text-danger"
                    }`}
                  >
                    {app.status}
                  </span>
                )}
                {app.status !== "accepted" && app.applicant?._id && app.onRoster !== false && (
                  <button
                    type="button"
                    onClick={() => handleAccept(slot._id, app.applicant._id)}
                    className="flex h-[2rem] w-[2rem] items-center justify-center rounded-kit-full bg-success-soft text-success transition-colors hover:bg-success-line"
                    title="accept"
                  >
                    <FiCheck size={14} />
                  </button>
                )}
                {app.status === "pending" && app.applicant?._id && (
                  <button
                    type="button"
                    onClick={() => handleDecline(slot._id, app.applicant._id)}
                    className="flex h-[2rem] w-[2rem] items-center justify-center rounded-kit-full bg-danger-soft text-danger transition-colors hover:bg-danger-line"
                    title="decline"
                  >
                    <FiX size={14} />
                  </button>
                )}
              </div>
            </div>
          );
        };

        return (
          <div
            key={slot._id}
            className="overflow-hidden rounded-kit-lg border-[1px] border-[var(--inyc-input-border-color)]"
          >
            <div
              className={`flex items-center justify-between gap-kit-16 bg-[var(--inyc-primary-light)] border-b-[1px] border-[var(--inyc-input-border-color)] ${
                compact ? "px-kit-12 py-kit-12" : "px-kit-20 py-kit-16"
              }`}
            >
              <div className="min-w-0">
                <div className="truncate font-['Primary-Bold'] text-kit-base text-[var(--inyc-primary-text-color)]">
                  {slot.title}
                </div>
                {slot.start && occurrenceStart && (
                  <div className="text-kit-2xs text-[var(--inyc-primary-text-color-light)]">
                    {formatSlotOccurrenceTimeRange(slot, occurrenceStart, eventMeta)}
                  </div>
                )}
                {slot.rosterName && (
                  <div className="mt-kit-6 flex min-w-0 flex-wrap gap-kit-4">
                    <RosterBubble
                      rosterId={slotRosterId}
                      onOpen={openRoster}
                      disabled={slot.rosterDeleted}
                      tone={isOutsideContextRoster || slot.rosterDeleted ? "warning" : "default"}
                      title={
                        isOutsideContextRoster
                          ? `this slot books from ${slot.rosterName}, not this roster`
                          : `this slot books from ${slot.rosterName}`
                      }
                      label={
                        slot.rosterDeleted
                          ? `${slot.rosterName} · deleted`
                          : isOutsideContextRoster
                          ? `${slot.rosterName} · other roster`
                          : slot.rosterName
                      }
                    />
                  </div>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-kit-10">
                <span
                  className={`rounded-kit-full px-kit-10 py-kit-4 text-kit-2xs font-['Primary-Medium'] ${
                    slot.status === "filled"
                      ? "bg-success-soft text-success"
                      : "bg-[var(--inyc-accent)]/20 text-[var(--inyc-accent)]"
                  }`}
                >
                  {slot.status}
                </span>
                {slot.status === "filled" && (
                  <button
                    type="button"
                    onClick={() => handleReopen(slot._id)}
                    title="cancel this booking and reopen the slot"
                    className="rounded-kit-full bg-danger-soft px-kit-12 py-kit-4 text-kit-2xs font-['Primary-Medium'] text-danger transition-colors hover:bg-danger-strong hover:text-on-fill"
                  >
                    reopen
                  </button>
                )}
              </div>
            </div>

            <div className="divide-y divide-[var(--inyc-input-border-color)]">
              {applications.length === 0 ? (
                <div className="px-kit-16 py-kit-16 text-kit-xs text-[var(--inyc-primary-text-color-light)]">
                  no applicants yet
                </div>
              ) : (
                <>
                  {acceptedApplications.map(renderApplication)}
                  {otherApplications.length > 0 && (
                    <>
                      {visibleOtherApplications.map(renderApplication)}
                      {(hiddenOtherCount > 0 || showOtherApplications) && (
                        <div className={compact ? "px-kit-12 py-kit-12" : "px-kit-20 py-kit-12"}>
                          <button
                            type="button"
                            onClick={() => toggleOtherApplications(slot._id)}
                            className={`w-full rounded-kit-md px-kit-12 py-kit-10 text-kit-xs font-['Primary-Medium'] transition-colors ${
                              showOtherApplications
                                ? "border-[1px] border-[var(--inyc-input-border-color)] text-[var(--inyc-primary-text-color-light)] hover:text-[var(--inyc-primary-text-color)]"
                                : "border-[1px] border-[var(--inyc-accent)] bg-[var(--inyc-accent)]/15 text-[var(--inyc-accent)] hover:bg-[var(--inyc-accent)]/25"
                            }`}
                          >
                            {showOtherApplications
                              ? "hide applicants"
                              : `view ${hiddenOtherCount} more applicant${hiddenOtherCount === 1 ? "" : "s"}`}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export { summarizeSlots };
export default LineupManager;
